import { FC, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Button, Skeleton } from '@hopskipdrive/react-components';
import PhoneEnabledIcon from '@mui/icons-material/PhoneEnabled';

import { Card } from '../../../../../shared/components/Card';
import { CopyToClipboard } from '../../../../../shared/components/CopyToClipboard';
import { colorPalette, colorTokens } from '../../../../../shared/components/variables';
import { useToast } from '../../../../../shared/contexts/ToastContext';
import { useIsMobileOrTablet } from '../../../../../shared/utils/helpers/util';
import { AssignDriverModal } from '../../../../components/AssignDriverModal';
import { DriverHeader } from '../../../../components/DriverHeader';
import { DriverInfoContainer } from '../../../../components/DriverInfoContainer';
import { DriverPersonalInfo } from '../../../../components/DriverPersonalInfo';
import { DriverSearch } from '../../../../components/DriverSearch';
import { useAssignDriverSubmit } from '../../../../hooks/useAssignDriverSubmit';
import { useDriverDetailsQuery } from '../../../../hooks/useDriverDetailsQuery';
import { useRemoveDriverFromCartSubmit } from '../../../../hooks/useRemoveDriverFromCartSubmit';
import { DriverSearchResult } from '../../../../types/driver';
import { RideDetails } from '../../../../types/rides';
import { Header, StyledAlert } from '../styles';

interface InCartDriverProps {
  rideDetails: RideDetails;
}
export const InCartDriver: FC<InCartDriverProps> = () => {
  const { rideSlug } = useParams();
  const { data: driverDetailsResult, isFetching } = useDriverDetailsQuery({ rideSlug: rideSlug || '' });
  const { addToast } = useToast();
  const [query, setQuery] = useState<string>('');
  const [driverQuery, setDriverQuery] = useState<string>('');
  const { mutate, isPending } = useRemoveDriverFromCartSubmit({
    rideSlug: rideSlug || '',
    successCallback: () => {},
    errorCallback: (error) => {
      addToast({
        severity: 'error',
        message: error.message,
        alertProps: {
          icon: false,
          onClose: undefined,
          style: {
            border: `1px solid ${colorTokens.error.outlineBorder}`,
            background: colorTokens.primary.contrast,
            color: colorTokens.error.main,
          },
        },
      });
    },
  });

  const [selectedDriver, setSelectedDriver] = useState<DriverSearchResult>();
  const [assignDriverModalOpen, setAssignDriverModalOpen] = useState<boolean>(false);
  const removeDriverFromCartSubmit = () => {
    mutate({ rideSlug: rideSlug });
  };
  const isMobileOrTablet = useIsMobileOrTablet();

  const { mutate: mutateAssignDriverSubmit, isPending: isPendingAssignDriverSubmit } = useAssignDriverSubmit({
    rideSlug: rideSlug,
    successCallback: () => {
      setAssignDriverModalOpen(false);

      addToast({ severity: 'success', message: 'Removed from cart' });
    },
    errorCallback: (error) => {
      setAssignDriverModalOpen(false);

      addToast({
        severity: 'error',
        message: error.message,
        alertProps: {
          icon: false,
          onClose: undefined,
          style: {
            border: `1px solid ${colorTokens.error.outlineBorder}`,
            background: colorTokens.primary.contrast,
            color: colorTokens.error.main,
          },
        },
      });
    },
  });

  const handleAssignDriverSubmit = (driverToSubmit) => {
    const assignDriverPayload: { slug: string; override_schedule_conflict?: string } = {
      slug: driverToSubmit.slug,
      override_schedule_conflict: 'true',
    };

    mutateAssignDriverSubmit(assignDriverPayload);
  };

  return isFetching ? (
    <Skeleton variant="text" width={690} height={50} data-testid="skeleton" />
  ) : (
    <Card>
      <Header>
        <Box display="flex" flexDirection="column" alignItems="left" gap="12px" width="100%">
          <Box width="100%">
            <DriverHeader driverDetails={driverDetailsResult?.data} hideSlug={true} />
          </Box>
          <Box display="flex" flexDirection="row" mb="20px" width="100%">
            <DriverSearch
              setSelectedDriver={setSelectedDriver}
              setAssignDriverModalOpen={setAssignDriverModalOpen}
              setQuery={setQuery}
              query={query}
              setDriverQuery={setDriverQuery}
              driverQuery={driverQuery}
            />
          </Box>
          <StyledAlert
            severity="warning"
            variant="outlined"
            message={`In Cart: ${driverDetailsResult?.data.firstName} ${driverDetailsResult?.data.lastName}`}
            action={
              <Button
                color="warning"
                size="small"
                onClick={() => removeDriverFromCartSubmit()}
                disabled={isPending}
                style={
                  isPending
                    ? {
                        border: 'none',
                        color: '#FFFFFF',
                        backgroundColor: colorTokens.text.disabled,
                      }
                    : {
                        border: `1px solid ${colorTokens.error.outlineBorder}`,
                        color: colorTokens.error.dark,
                        backgroundColor: colorPalette.red975,
                      }
                }
              >
                {isPending ? 'Removing...' : 'Remove from cart'}
              </Button>
            }
            style={{ fontSize: '20px' }}
          />
          <Box display="flex" alignItems="center" gap="12px">
            <CopyToClipboard
              toastMessage="Slug Copied"
              text={driverDetailsResult?.data.slug || ''}
              variant="h6"
              color={colorTokens.text.secondary}
            />
          </Box>
        </Box>
      </Header>
      <Box display="flex" gap="20px" sx={{ width: '100%' }} flexDirection={isMobileOrTablet ? 'column' : 'row'}>
        <Box sx={{ width: isMobileOrTablet ? '100%' : '50%' }} alignItems="flex-end">
          <DriverPersonalInfo driverDetails={driverDetailsResult?.data} isFetching={isFetching} />
          <Box mb={3}>
            <DriverInfoContainer isFetching={isFetching}>
              <PhoneEnabledIcon style={{ color: colorTokens.text.secondary }} />
              <CopyToClipboard
                toastMessage="Phone Number Copied"
                text={driverDetailsResult?.data.mobilePhoneNumber || ''}
                variant="h6"
                color={colorTokens.text.secondary}
              />
            </DriverInfoContainer>
          </Box>
        </Box>
      </Box>
      {selectedDriver && (
        <AssignDriverModal
          isOpen={assignDriverModalOpen}
          isPending={isPendingAssignDriverSubmit}
          selectedDriver={selectedDriver}
          setQuery={setQuery}
          setDriverQuery={setDriverQuery}
          setSelectedDriver={setSelectedDriver}
          setAssignDriverModalOpen={setAssignDriverModalOpen}
          handleAssignDriverSubmit={handleAssignDriverSubmit}
        />
      )}
    </Card>
  );
};
